import React, { useContext, useState, useEffect } from "react";
import PropTypes from "prop-types";
import NextRouter from "next/router";
import api from "components/settings/api";
import { GlobalContext } from "../../context/GlobalContext";
import {
  Button,
  Card,
  CardContent,
  Typography,
  Avatar,
  Grid,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import moment from "moment";
import { OrganizationSticker, PremiumTeamSticker } from "styles/globalSVG";

const useStyles = makeStyles((theme) => ({
  root: {
    width: 207,
    height: "100%",
    backgroundColor: "#ffffff",
    boxShadow: "none",
    border: "1px solid rgba(0, 27, 17, 0.1)",
    borderRadius: 12,
  },
  avatarDiv: {
    display: "contents",
  },
  avatar: {
    backgroundColor: "#F8C22B",
    height: 80,
    width: 80,
    marginBottom: 10,
  },
  cardMargin: {
    marginBottom: theme.spacing(0.5),
  },
  cardHeader: {
    textAlign: "center",
    padding: "5px",
  },
  cardTitle: {
    fontFamily: "Poppins",
    fontWeight: 600,
    fontSize: 20,
    textAlign: "center",
    letterSpacing: 0.15,
    overflowWrap: "break-word",
  },
  cardSubtitle: {
    fontFamily: "Open Sans",
    fontWeight: 400,
    fontSize: 12,
    letterSpacing: 0.15,
  },
  cardCat: {
    fontFamily: "Open Sans",
    fontWeight: 700,
    fontSize: 13,
    color: "#001B11",
  },
  cardText: {
    color: "rgba(0, 27, 17, 0.7)",
    fontFamily: "Open Sans",
    margin: theme.spacing(1),
    fontSize: 13,
    fontWeight: 600,
  },
  cardFooter: {
    color: "rgba(0, 27, 17, 0.6)",
    fontFamily: "Open Sans",
    fontSize: 13,
    fontWeight: 400,
    display: "flex",
    flexDirection: "column",
  },
  created: {
    fontStyle: "italic",
    paddingBottom: 5,
    fontSize: 13,
  },
  numberTeamMates: {
    paddingBottom: 5,
    fontSize: 13,
  },
}));
const MyTeamCard = ({ team, publicUser, publicUserJoin, visitor }) => {
  const appState = useContext(GlobalContext);
  const { currentUser } = appState;
  const classes = useStyles();
  // Can this user see new apps message
  const [canSeeNewApps, setCanSeeNewApps] = useState(false);
  // Flag to determine if this team has new applications to review
  const [hasNewApps, setHasNewApps] = useState(false);

  // This effect monitors the state of loading team data
  useEffect(() => {
    // Determine if the user is the creator
    const determineUserRole = () => {
      // Make use the User object has loaded
      if (team.Team.User !== undefined) {
        // The user of this session
        const userId = currentUser?.public_user_id;
        // The creator of the team
        let creatorId = "";

        // If creator deleted account, User will equal null
        if (team.Team.User === null) {
          creatorId = "deletedId";
        } else {
          creatorId = team.Team.User.public_user_id;
        }

        // Determine if this user is the creator
        if (userId === creatorId) {
          setCanSeeNewApps(true);
        }
      }
    };

    if (team.Team?.public_team_id && currentUser !== undefined) {
      determineUserRole();
    }
  }, [team, currentUser]);

  // On mount check for new apps
  useEffect(() => {
    const checkForNewApplications = async () => {
      try {
        const res = await api.get(
          `/api/v1/invites/team/applications/${team.Team.public_team_id}`
        );

        // If the data return has more than an empty array.
        if (res.data.length > 0) {
          setHasNewApps(true);
        }
      } catch (err) {
        throw new Error(err);
      }
    };

    if (canSeeNewApps) {
      checkForNewApplications();
    }
  }, [canSeeNewApps, team]);

  // This method sets the id and then loads the team page.
  const goToTeamPage = () => {
    // Move to team page
    const route = team.Team?.type === "simple sub-team" ? "/subteam" : "/team";
    if (!publicUser && !publicUserJoin && !visitor) {
      NextRouter.push({
        pathname: `${route}/${team.Team?.public_team_id}`,
        query: { number: team.Team.id },
      });
    } else {
      NextRouter.push(
        `/publicteam/${team.public_team_id || team.Team?.public_team_id}`
      );
    }
  };

  let teamManagerName = "";

  if (team.Team?.User) {
    teamManagerName = team.Team.User.fullname;
  } else {
    teamManagerName = "No longer exist";
  }

  const imageUrl =
    team?.Team?.picture?.includes(
      "res.cloudinary.com/teammateme-com/image/upload/v1712259855/UserAvatar/x7kgrpnhq7vx5yze7krx.svg"
    ) || team?.Team?.picture?.includes("https://avatars.dicebear.com/api/")
      ? `https://api.dicebear.com/9.x/icons/svg`
      : team?.Team?.picture?.replace(/^http:/, "https:") ||
        team?.picture?.replace(/^http:/, "https:") ||
        "https://res.cloudinary.com/teammateme/image/upload/v1701306221/UserAvatar/csy8csv1lwdahg0kwfql.png";

  return (
    <div>
      <Button
        style={{ overflow: "hidden" }}
        onClick={goToTeamPage}
        aria-label="View team"
      >
        <Grid container direction="column">
          <Card className={classes.root}>
            <CardContent>
              <div
                style={{
                  position: "absolute",
                  top: "5px",
                  right: "5px",
                  overflow: "hidden",
                }}
              >
                {team?.Team?.type === "premium" && (
                  <span className={classes.stickerStyle}>
                    <PremiumTeamSticker />
                  </span>
                )}
              </div>
              <div
                style={{
                  position: "absolute",
                  top: "5px",
                  right: "5px",
                  overflow: "hidden",
                }}
              >
                {team?.Team?.type === "organization" && (
                  <span className={classes.stickerStyle}>
                    <OrganizationSticker />
                  </span>
                )}
              </div>
              <Grid container className={classes.cardMargin}>
                <Grid container direction="row" justifyContent="center">
                  <Grid item xs={12} className={classes.avatarDiv}>
                    <Avatar
                      aria-label="team"
                      className={classes.avatar}
                      component="div"
                      //Updated Team Images
                      src={imageUrl}
                      // src={
                      //   team?.Team?.picture?.replace(/^http:/, "https:") ||
                      //   team?.picture?.replace(/^http:/, "https:") ||
                      //   "https://res.cloudinary.com/teammateme/image/upload/v1701306221/UserAvatar/csy8csv1lwdahg0kwfql.png"
                      // }

                      alt=""
                    />
                  </Grid>
                  <Grid item xs={12} className={classes.cardHeader}>
                    <Typography
                      variant="h5"
                      className={classes.cardTitle}
                      aria-label="Team Title"
                    >
                      {team.Team?.title || team.title}
                    </Typography>
                  </Grid>
                  <Grid
                    container
                    direction="column"
                    justifyContent="center"
                    className={classes.cardFooter}
                  >
                    <div>
                      <Grid item xs={12}>
                        <Typography
                          className={classes.created}
                          aria-label="Created on"
                        >
                          Created on{" "}
                          {moment(
                            team.Team?.createdAt || team.createdAt
                          ).format("MMMM Do, YYYY")}
                        </Typography>
                      </Grid>
                    </div>
                    <div>
                      <Grid item xs={12}>
                        <Typography className={classes.numberTeamMates}>
                          {(team.Team?.Memberships?.length ||
                            team.Memberships?.length) === 1
                            ? "1 member"
                            : (team.Team?.Memberships?.length ||
                                team.Memberships?.length) + " members"}
                        </Typography>
                      </Grid>
                    </div>
                  </Grid>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Button>
    </div>
  );
};

export default MyTeamCard;
MyTeamCard.propTypes = {
  team: PropTypes.object,
};
